import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table } from 'react-bootstrap';
import { postData } from '../../utility/Utility';
import { toast } from 'react-toastify';
import CustomLoader from '../../Components/CustomLoader/CustomLoader';

export default function SubcategoryModal({ 
  show, 
  onClose, 
  onSelect, 
  categoryId,
  selectedId = '' 
}) {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  const fetchSubcategories = useCallback(async () => {
    if (!categoryId) return;
    
    try {
      setLoading(true);
      const response = await postData('api/subcategory/list', {
        category: categoryId,
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm
      });
      
      if (response?.status) {
        setSubcategories(response?.data || []);
        setPagination(prev => ({
          ...prev,
          total: response?.totalItems || 0,
          totalPages: response?.totalPages || 1
        }));
      } else {
        throw new Error(response.data?.message || 'Failed to fetch subcategories');
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          'Error loading subcategories'
      );
    } finally {
      setLoading(false);
    }
  }, [categoryId, pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    if (show && categoryId) {
      fetchSubcategories();
    } else {
      setSubcategories([]);
    }
  }, [show, categoryId, searchTerm, pagination.page, pagination.limit, fetchSubcategories]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubcategories();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Subcategory</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <form onSubmit={handleSearch} className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search subcategories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>
        </div>
        
        <div className="table-responsive">
          <Table hover>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Image</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!categoryId ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    Please select a category first
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <CustomLoader loading={true} />
                  </td>
                </tr>
              ) : subcategories.length > 0 ? (
                subcategories.map((subcategory, index) => (
                  <tr 
                    key={subcategory._id} 
                    className={selectedId === subcategory._id ? 'table-active' : ''}
                  >
                    <td>{index + 1}</td>
                    <td>
                      <img
                        src={subcategory?.image}
                        alt={subcategory.title}
                        style={{ width: 50, height: 50, objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/path/to/default-image.png';
                        }}
                        className="rounded"
                      />
                    </td>
                    <td>{subcategory.title}</td>
                    <td>{subcategory.slug}</td>
                    <td>
                      <span className={`badge bg-${subcategory.status === 'active' ? 'success' : 'danger'}`}>
                        {subcategory.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => onSelect(subcategory)}
                        disabled={selectedId === subcategory._id}
                      >
                        {selectedId === subcategory._id ? 'Selected' : 'Select'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No subcategories found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
    </Modal>
  );
}
